import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp } from "iconsax-react";
import { Skeleton } from "@/shared/ui";
import type { Locale } from "@/shared/utils";
import { formatMoneyKzt, formatDateForDisplay } from "@/shared/utils";
import type { BankTransaction } from "../../../types";

interface TransactionListProps {
  transactions: BankTransaction[];
  isLoading: boolean;
  locale: Locale;
}

function formatAmount(tx: BankTransaction, locale: Locale): string {
  const sign = tx.type === "DEBIT" ? "- " : "+ ";
  if (tx.amount.currency === "KZT") {
    return `${sign}${formatMoneyKzt(tx.amount.amount, locale)}`;
  }
  return `${sign}${tx.amount.amount.toLocaleString()} ${tx.amount.currency}`;
}

export default function TransactionList({ transactions, isLoading, locale }: TransactionListProps) {
  const { t } = useTranslation("BankingPage");

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="radius-lg border surface-base-fill surface-base-stroke p-4">
            <div className="flex items-center gap-4">
              <Skeleton circle width={40} height={40} />
              <div className="flex-1">
                <Skeleton height={16} width={200} className="mb-2" />
                <Skeleton height={14} width={150} />
              </div>
              <Skeleton height={20} width={120} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-2">
        <p className="text-body-bold-lg content-base-primary">{t("empty.title")}</p>
        <p className="text-body-regular-sm content-base-secondary">{t("empty.description")}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {transactions.map((tx) => (
        <div key={tx.id} className="radius-lg border surface-base-fill surface-base-stroke p-4 flex items-center gap-4">
          <div
            className={`w-10 h-10 radius-sm flex items-center justify-center text-white radial-glow ${
              tx.type === "CREDIT" ? "background-positive-fill" : "background-negative-fill"
            }`}>
            {tx.type === "CREDIT" ? (
              <ArrowDown size={20} color="currentColor" />
            ) : (
              <ArrowUp size={20} color="currentColor" />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-body-bold-md content-base-primary truncate">{tx.description}</p>
            <p className="text-body-regular-sm content-base-secondary truncate">{tx.correspondentName}</p>
          </div>

          <div className="text-right shrink-0">
            <p
              className={`text-body-bold-md ${tx.type === "CREDIT" ? "content-action-positive" : "content-action-negative"}`}>
              {formatAmount(tx, locale)}
            </p>
            <p className="text-body-regular-sm content-base-secondary">{formatDateForDisplay(tx.operDate)}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

